/**
 * Модуль для управления UI авторизации
 */
import auth from './auth.js';
import { enterFullscreen } from './utils/fullscreen.js';
import app from './app.js';
import { errorLogger } from './errorLogger.js';
import { translate } from './translation.js';

class AuthUI {
    constructor() {
        this.authModal = document.getElementById('authModal');
        this.mainContainer = document.getElementById('mainContainer');
        this.setupEventListeners();
        this.checkAuth();
    }

    setupEventListeners() {
        // Форма входа
        document.getElementById('loginBtn').addEventListener('click', () => this.handleLogin());
        document.getElementById('registerBtn').addEventListener('click', () => this.handleRegister());
        document.getElementById('logoutBtn').addEventListener('click', () => this.handleLogout());

        // Переключение форм
        document.getElementById('showRegister').addEventListener('click', (e) => {
            e.preventDefault();
            this.showRegisterForm();
        });
        document.getElementById('showLogin').addEventListener('click', (e) => {
            e.preventDefault();
            this.showLoginForm();
        });

        // Enter key support
        ['loginUsername', 'loginPassword', 'registerUsername', 'registerPassword', 
         'registerPasswordConfirm'].forEach(id => {
            document.getElementById(id).addEventListener('keypress', (e) => {
                if (e.key === 'Enter') {
                    if (id.startsWith('login')) {
                        this.handleLogin();
                    } else if (id.startsWith('register')) {
                        this.handleRegister();
                    }
                }
            });
        });
    }

    async checkAuth() {
        if (auth.isLoggedIn()) {
            this.showApp();
            
            // Проверяем: если пользователь guest_XXXX уже сохранен в БД - предлагаем переименовать
            if (auth.currentUser && 
                auth.currentUser.id && 
                auth.currentUser.name && 
                auth.currentUser.name.startsWith('guest_')) {
                // Пользователь в БД (есть id) с гостевым именем → показываем окно профиля
                setTimeout(() => {
                    if (app && typeof app.showProfileEditModal === 'function') {
                        app.showProfileEditModal();
                    }
                }, 1000);
            }
        } else {
            // Автоматически создаём локального гостевого пользователя
            const result = auth.createGuestUser();
            if (result.success) {
                this.showApp();
                
                // Пытаемся войти в полноэкранный режим
                try {
                    await enterFullscreen();
                } catch (err) {
                    console.warn('Не удалось войти в полноэкранный режим:', err);
                }
                
                // Инициализируем приложение
                if (app && typeof app.reinitAfterLogin === 'function') {
                    await app.reinitAfterLogin();
                }
            } else {
                // Если не удалось создать гостя, показываем форму авторизации
                this.showAuth();
            }
        }
    }

    showAuth() {
        if (this.authModal) this.authModal.style.display = 'flex';
        if (this.mainContainer) this.mainContainer.style.display = 'none';
        this.showLoginForm();
    }

    showApp() {
        if (this.authModal) this.authModal.style.display = 'none';
        if (this.mainContainer) this.mainContainer.style.display = 'block';
        
        // Применяем масштабирование для Telegram после показа контейнера
        if (typeof window.Telegram !== 'undefined' && window.Telegram.WebApp && app && typeof app.applyTelegramScaling === 'function') {
            setTimeout(() => {
                app.applyTelegramScaling();
            }, 100);
        }
    }

    showLoginForm() {
        document.getElementById('loginForm').style.display = 'block';
        document.getElementById('registerForm').style.display = 'none';
        document.getElementById('authTitle').textContent = translate('Вход');
        this.hideError();
    }

    showRegisterForm() {
        document.getElementById('loginForm').style.display = 'none';
        document.getElementById('registerForm').style.display = 'block';
        document.getElementById('authTitle').textContent = translate('Регистрация');
        this.hideError();
    }

    showError(message, isError = true) {
        const errorEl = document.getElementById('authError');
        if (errorEl) {
            errorEl.textContent = message;
            errorEl.style.display = 'block';
            errorEl.style.color = isError ? '#ff5252' : '#4caf50';
        }
    }

    hideError() {
        const errorEl = document.getElementById('authError');
        if (errorEl) {
            errorEl.style.display = 'none';
        }
    }

    async handleLogin() {
        const username = document.getElementById('loginUsername').value.trim();
        const password = document.getElementById('loginPassword').value;

        if (!username || !password) {
            this.showError(translate('Заполните все поля'));
            return;
        }

        const loginBtn = document.getElementById('loginBtn');
        loginBtn.disabled = true;
        loginBtn.textContent = translate('Вход...');

        const result = await auth.login(username, password);

        loginBtn.disabled = false;
        loginBtn.textContent = translate('Войти');

        if (result.success) {
            this.showApp();
            
            // Входим в полноэкранный режим после успешного входа
            // Важно: вызов должен быть в контексте пользовательского действия (клик)
            try {
                await enterFullscreen();
            } catch (err) {
                console.warn('Не удалось войти в полноэкранный режим:', err);
                // Ошибка уже отправлена на сервер в enterFullscreen, но логируем для контекста
                errorLogger.logError({
                    error_type: 'fullscreen_error',
                    message: `Не удалось войти в полноэкранный режим после входа: ${err.message || String(err)}`,
                    stack: err.stack,
                    url: window.location.href,
                    additional_info: {
                        error_name: err.name,
                        context: 'after_login'
                    }
                });
            }
            
            // Переинициализируем приложение без перезагрузки страницы (SPA)
            await app.reinitAfterLogin();
        } else {
            this.showError(result.error || translate('Ошибка при входе'));
        }
    }

    async handleRegister() {
        const username = document.getElementById('registerUsername').value.trim();
        const password = document.getElementById('registerPassword').value;
        const passwordConfirm = document.getElementById('registerPasswordConfirm').value;

        if (!username || !password || !passwordConfirm) {
            this.showError(translate('Заполните все поля'));
            return;
        }

        if (password !== passwordConfirm) {
            this.showError(translate('Пароли не совпадают'));
            return;
        }

        if (password.length < 6) {
            this.showError(translate('Пароль должен быть не менее 6 символов'));
            return;
        }

        const registerBtn = document.getElementById('registerBtn');
        registerBtn.disabled = true;
        registerBtn.textContent = translate('Регистрация...');

        const result = await auth.register(username, password);

        registerBtn.disabled = false;
        registerBtn.textContent = translate('Зарегистрироваться');

        if (result.success) {
            this.showError(translate('Регистрация успешна! Теперь вы можете войти.'), false);
            // Очищаем форму
            document.getElementById('registerUsername').value = '';
            document.getElementById('registerPassword').value = '';
            document.getElementById('registerPasswordConfirm').value = '';
            // Переключаем на форму входа
            setTimeout(() => {
                document.getElementById('loginUsername').value = username;
                this.showLoginForm();
            }, 2000);
        } else {
            this.showError(result.error || translate('Ошибка при регистрации'));
        }
    }


    handleLogout() {
        // Сначала показываем окно авторизации для повторного входа
        auth.logout();
        this.showAuth();
        // Можно перезагрузить страницу или оставить так
        // window.location.reload();
    }
}

export default AuthUI;

